import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { categoryTint } from "@/lib/categoryColors";
import { FileText, Layers, ArrowRight, Crown, FilePen, CreditCard, CheckCircle2, Clock } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [cats, setCats] = useState({ categories: [], total: 0 });
  const [subs, setSubs] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const subscribed = user && (["active", "trialing"].includes(user.subscription_status) || user.role === "admin");
  const trialing = user && user.subscription_status === "trialing";
  const trialDaysLeft = trialing && user.subscription_expires_at
    ? Math.max(0, Math.ceil((new Date(user.subscription_expires_at) - new Date()) / 86400000)) : 0;

  useEffect(() => {
    api.get("/forms/categories").then(({ data }) => setCats(data)).catch(() => {});
    if (subscribed) api.get("/submissions").then(({ data }) => setSubs(data)).catch(() => {});
  }, [subscribed]);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Billing portal unavailable");
      setPortalBusy(false);
    }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <p className="overline text-accent mb-2">{t("dash.eyebrow")}</p>
        <h1 className="font-serif font-bold text-5xl tracking-tighter mb-8">{t("dash.welcome", { name: user?.name?.split(" ")[0] || "" })}</h1>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="grid-card p-6 md:col-span-2 flex items-center justify-between" data-testid="subscription-card">
            <div>
              <p className="overline text-muted-foreground mb-2">{t("dash.membership")}</p>
              <div className="flex items-center gap-3">
                <span className={`grid place-items-center w-11 h-11 ${subscribed ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}><Crown size={20} /></span>
                <div>
                  <p className="font-serif font-bold text-2xl leading-none text-foreground">{trialing ? t("dash.freeTrial") : subscribed ? t("dash.proMember") : t("dash.freePlan")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {trialing
                      ? t("dash.daysLeft", { days: trialDaysLeft })
                      : subscribed
                        ? (user.role === "admin" ? t("dash.fullAccessAdmin") : t("dash.planActive", { plan: user.subscription_plan === "annual" ? t("dash.annual") : t("dash.monthly") }))
                        : t("dash.upgradePrompt")}
                  </p>
                </div>
              </div>
            </div>
            {(trialing || !subscribed) && <Link to="/pricing"><Button data-testid="dash-upgrade-btn" className="rounded-sm bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors gap-2">{trialing ? t("dash.upgradeNow") : t("dash.upgrade")} <ArrowRight size={16} /></Button></Link>}
            {subscribed && !trialing && user.role !== "admin" && user.stripe_customer_id && (
              <Button onClick={openPortal} disabled={portalBusy} variant="outline" data-testid="manage-billing-btn" className="rounded-sm gap-2 hover:bg-secondary">
                <CreditCard size={16} /> {portalBusy ? t("dash.opening") : t("dash.manageBilling")}
              </Button>
            )}
          </div>
          <div className="grid-card p-6">
            <p className="overline text-muted-foreground mb-2">{t("dash.availableForms")}</p>
            <p className="font-serif font-bold text-4xl text-foreground">{cats.total}</p>
            <Link to="/forms" className="text-sm text-accent hover:underline mt-2 inline-flex items-center gap-1">{t("dash.browseAll")} <ArrowRight size={14} /></Link>
          </div>
        </div>

        {/* Continue where you left off */}
        {subscribed && subs.length > 0 && (() => {
          const resume = subs.find((s) => s.filing_status !== "filed") || subs[0];
          return (
            <Link to={`/forms/${resume.form_number}?submission=${resume.id}`} data-testid="resume-card"
              className="border-[3px] border-black bg-accent text-accent-foreground p-6 flex items-center justify-between gap-4 mb-10 group transition-transform hover:-translate-y-1">
              <div className="flex items-center gap-4 min-w-0">
                <span className="grid place-items-center w-11 h-11 bg-accent-foreground text-accent shrink-0"><Clock size={20} /></span>
                <div className="min-w-0">
                  <p className="overline mb-1">{t("dash.resumeTitle")}</p>
                  <p className="font-serif font-bold text-xl leading-tight truncate">{resume.form_number} · {resume.title}</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 font-medium shrink-0">{t("dash.resumeCta")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          );
        })()}

        {/* Categories */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          {cats.categories.map((c) => (
            <Link key={c.name} to={`/forms?category=${encodeURIComponent(c.name)}`}
              style={{ backgroundColor: categoryTint(c.name) }}
              className="border-[3px] border-black p-6 flex items-center justify-between group text-black transition-transform hover:-translate-y-1">
              <div className="flex items-center gap-3"><Layers /><div><p className="font-serif font-bold text-xl">{c.name}</p><p className="text-sm text-black/70">{t("dash.forms", { count: c.count })}</p></div></div>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>

        {/* Saved forms */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl tracking-tight">{t("dash.savedTitle")}</h2>
          {subs.length > 0 && <Link to="/saved" className="text-sm text-accent hover:underline">{t("dash.viewAll")}</Link>}
        </div>
        {!subscribed ? (
          <div className="grid-card p-10 text-center">
            <FilePen className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">{t("dash.upgradeToSave")}</p>
          </div>
        ) : subs.length === 0 ? (
          <div className="grid-card p-10 text-center">
            <FileText className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">{t("dash.noSaved")}</p>
            <Link to="/forms"><Button variant="outline" className="rounded-sm">{t("dash.startForm")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subs.slice(0, 6).map((s) => (
              <Link key={s.id} to={`/forms/${s.form_number}?submission=${s.id}`} data-testid={`saved-${s.id}`} className="grid-card p-5 group">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="rounded-sm font-mono text-xs">{s.form_number}</Badge>
                  {s.filing_status === "filed" && (
                    <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-emerald-700" data-testid={`dash-status-${s.id}`}>
                      <CheckCircle2 size={13} /> {t("dash.filed")}
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-lg leading-tight mb-2">{s.title}</h3>
                <p className="text-xs text-muted-foreground">{t("dash.updated", { date: new Date(s.updated_at).toLocaleDateString() })}</p>
              </Link>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
